<template>
    <div class="frequent-operations-wrapper" :class="{'mobile': getMobileAppState, 'white-background': whiteBackground}">
        <div class="title">{{t('home.widgets.frequentOperations.title')}}</div>
        <!-- Web Display -->
        <div v-if="!showLoader && !errorState && getFrequentOperations.length && !getMobileAppState" class="cards-wrapper">
            <frequent-operation-card
            v-for="card in getFrequentOperations"
            :key="card.operation.id"
            :card="card"
            @cardClickHandler="cardClickHandler"/>
        </div>
        <!-- Mobile Generic Slider -->
        <slider-generic
        v-if="!showLoader && !errorState && getFrequentOperations.length && getMobileAppState"
        :selectedIndex="selectedCardIndex"
        :sliderList="getFrequentOperations"
        @updateSelectedIndex="selectedCardIndex = $event">
            <frequent-operation-card
            v-for="card in getFrequentOperations"
            :key="card.operation.id"
            :card="card"
            @cardClickHandler="cardClickHandler"/>
        </slider-generic>
        <!-- Empty-state -->
        <div v-if="!showLoader && !errorState && !getFrequentOperations.length" class="empty-state">
            <no-movements-icon/>
            <div class="message">{{ t('home.widgets.frequentOperations.emptyStateMessage') }}</div>
        </div>
        <!-- Error State -->
        <empty-state-error v-if="!showLoader && errorState" :messsage="t('home.errorMessage')"/>
        <!-- Loader -->
        <loader-generic
        v-if="showLoader || showOverlayLoader"
        :isOverlay="showOverlayLoader"
        style="margin: auto;"/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
    </div>
</template>

<script setup>
import SliderGeneric from '@/components/generic/sliders/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import FrequentOperationCard from '@/components/features/home/widgets/frequentOperations/OperationCard'
import NoMovementsIcon from '@/assets/icons/illustrations/emptyState/no-movements.svg?inline'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { responseRouteHandler } from '@/assets/scripts/mappers/frequentOperations'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@whiteBackground {Boolean} - Prop to apply white background color
*/
const props = defineProps({
    whiteBackground: Boolean
})

/* mapActions & mapGetters */
const { requestFrequentOperations, requestTransfersDetail, requestSmartTransfersDetail, requestPaymentsDetail, requestCardlessWithdrawDetail, requestInternationalTransferDetail, requestTopupsTransferDetail, requestMobilePaymentDetail } = mapActions(moduleMapper.HOME)
const { getFrequentOperations } = mapGetters(moduleMapper.HOME)
const { getPaymentsOperatorsList } = mapGetters(moduleMapper.PAYMENTS_SERVICES)
const { getMobileAppState } = mapGetters()

const showLoader = ref(false)
const showOverlayLoader = ref(false)
const selectedCardIndex = ref(0)

const cardClickHandler = async (card) => {
    showOverlayLoader.value = true

    const types = {
        TRANSFER: requestTransfersDetail,
        PAYMENT: requestPaymentsDetail,
        CARDLESS_WITHDRAWAL: requestCardlessWithdrawDetail,
        INTERNATIONAL_TRANSFER: requestInternationalTransferDetail,
        PRE_PAID_CREDIT_CARD_TOP_UP: requestTopupsTransferDetail,
        SMART_TRANSFER: requestSmartTransfersDetail,
        PRE_PAID_CREDIT_CARD_TOP_UP_EXTERNAL: requestTopupsTransferDetail,
        PRE_PAID_CARD_TOP_UP_EXTERNAL: requestTopupsTransferDetail,
        MOBILE_PAYMENT: requestMobilePaymentDetail
    }

    const typesPayload = {
        TRANSFER: card.operation.id,
        PAYMENT: { paymentId: card.operation.id },
        CARDLESS_WITHDRAWAL: { cardId: card.operation.cardNumber, codeId: card.operation.id },
        INTERNATIONAL_TRANSFER: card.operation.id,
        PRE_PAID_CREDIT_CARD_TOP_UP: { customerOperationId: card.operation.id },
        SMART_TRANSFER: card.operation.id,
        PRE_PAID_CREDIT_CARD_TOP_UP_EXTERNAL: { customerOperationId: card.operation.id },
        PRE_PAID_CARD_TOP_UP_EXTERNAL: { customerOperationId: card.operation.id },
        MOBILE_PAYMENT: card.operation.id
    }
    try {
        const res = await types[card.operation.type](typesPayload[card.operation.type])
        const operationType = card.operation.type

        if (operationType === 'PAYMENT' && (res.data.data.paymentsOperator.flow === 'emis_special_service' || res.data.data.paymentsOperator.flow === 'emis_service' || res.data.data.paymentsOperator.flow === 'emis_voucher')) {
            emisPaymentInfoHandler(res, card.operation.type)
        } else navigateByName(responseRouteHandler(res, card.operation.type), { params: res.data.data })
    } catch (error) {
        errorHandler(error.response.data, () => { cardClickHandler(card) })
    } finally {
        showOverlayLoader.value = false
    }
}

const emisPaymentInfoHandler = (response, cardType) => {
    const responseData = response.data.data

    /* Parsed Payments Operators List Names */
    const parsedPaymentsOperatorsListNames = []
    getPaymentsOperatorsList.value.forEach(operatorGroups => {
        operatorGroups.children.forEach(operator => {
            operator.children.forEach(elem => {
                elem.parentName = operator.name
                elem.operatorGroupName = operatorGroups.name
                parsedPaymentsOperatorsListNames.push(elem)
            })
        })
    })

    const operatorInfo = parsedPaymentsOperatorsListNames.find(el => el.id === responseData.paymentsOperator.id)

    navigateByName(responseRouteHandler(response, cardType), { params: { ...responseData, ...operatorInfo } })
}

const initialActions = async () => {
    showLoader.value = true
    errorState.value = false

    try {
        await requestFrequentOperations({ size: 4 })
    } catch (error) {
        errorState.value = true
    } finally {
        showLoader.value = false
    }
}

/*************************************************
*                                                *
*                  Error Handlers                *
*                                                *
*************************************************/
const errorState = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialActions()
</script>

<style lang="scss" scoped>
.frequent-operations-wrapper {
    width: 100%;
    max-width: 1636px;
    height: 168px;
    padding: 16px;
    margin: 0 auto;

    display: flex;
    flex-direction: column;
    align-items: center;

    background-color: var(--secondary-light-3);
    border-radius: 16px;
    z-index: 1;

    .title {
        width: 100%;
        margin-bottom: 16px;

        @include H6-Medium;
        color: var(--secondary-dark-3);
    }

    .cards-wrapper {
        width: 100%;

        display: flex;
        gap: 8px;
    }

    .empty-state {
        width: 100%;
        height: 100%;

        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;

        @include H7-Regular;
        line-height: 16px;

        color: var(--secondary-dark-2);

        svg {
            width: 120px;
            height: 120px;
            flex-shrink: 0;
        }
    }

    &.mobile {
        height: auto;
        padding: 16px 0;

        .title {
            margin-bottom: 0;
            padding: 0 16px;
        }

        :deep(.slider-content-wrapper) .slider-wrapper {
            padding: 16px 16px 0 16px;
        }

        .empty-state {
            padding: 0 16px;
            gap: 8px;

            svg {
                width: 80px;
                height: 80px;
                flex-shrink: 0;
            }
        }
    }

    &.white-background {
        background-color: var(--secondary-light-4);

        .title {
            color: var(--primary)
        }
    }
}
</style>
